import React, { createContext, useContext, useEffect, useState } from 'react';
import authService from '../api/authService';
import MessagingService from '../services/MessagingServices';
import { performLogout, registerAuthLogoutHandlers } from '../services/logoutService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    registerAuthLogoutHandlers({ setUser, setError });
    return () => registerAuthLogoutHandlers(null);
  }, []);

  useEffect(() => {
    // Check authentication on app start
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            // Check sessionStorage for user data
            const storedUser = sessionStorage.getItem('authUser');
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
              } catch (e) {
                setUser(currentUser);
              }
            } else {
              setUser(currentUser);
            }
          }
        }
      } catch (err) {
        console.error('[AuthContext] Error checking auth:', err);
        setError(err.message);
        await performLogout({ reason: 'auth-bootstrap-error' });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Set up token expiration check interval
    const checkInterval = setInterval(() => {
      if (!authService.isAuthenticated()) {
        setUser(null);
        setError('Session expired');
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(checkInterval);
  }, []); // Remove user dependency to prevent infinite loop

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authService.login(email, password);
      const user = authService.getCurrentUser();
      if (user) {
        setUser(user);
        const userJson = JSON.stringify(user);
        // Store in both localStorage and sessionStorage
        // localStorage.setItem('authUser', userJson);
        sessionStorage.setItem('authUser', userJson);
        
        // CRITICAL: Register FCM token immediately after successful login
        // This ensures user receives real-time notifications
        try {
          await MessagingService.registerTokenAfterLogin();
          //console.log("✅ FCM token registered after login");
        } catch (fcmError) {
          console.warn("⚠️ FCM token registration failed (non-critical):", fcmError);
          // Don't fail login if FCM registration fails
        }
        
        // CRITICAL: Dispatch login event to trigger notification fetch
        // This ensures NotificationContext fetches notifications immediately after login
        window.dispatchEvent(new CustomEvent('user-logged-in'));
        //console.log("🔔 Login event dispatched - NotificationContext will fetch notifications");
        
        // Also set flag for polling mechanism
        sessionStorage.setItem("wasAuthenticated", "true");
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (options = {}) => performLogout(options);

  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role || user.roles?.includes(role);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // For now, return true if user has role
    // Permission checking should be implemented based on backend permissions
    return !!user.role;
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      // Note: This is for regular password change, not first-time
      // First-time password change is handled in ChangePassword component
      // This method may need to be updated when backend endpoint is available
      throw new Error('Regular password change not yet implemented');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      // Note: Profile update endpoint not yet available
      // This is a placeholder for future implementation
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
      const userJson = JSON.stringify(updatedUser);
      // Store in both localStorage and sessionStorage
      // localStorage.setItem('authUser', userJson);
      sessionStorage.setItem('authUser', userJson);
      return { user: updatedUser };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const clearError = () => setError(null);

  const getDemoAccounts = () => {
    // Return demo accounts for testing (if needed)
    return [];
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    hasPermission,
    changePassword,
    updateProfile,
    clearError,
    getDemoAccounts,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Additional hooks for specific use cases
export const useAuthUser = () => {
  const { user } = useAuth();
  return user;
};

export const useAuthRole = () => {
  const { user, hasRole } = useAuth();
  return { role: user?.role, hasRole };
};

export const useAuthPermission = () => {
  const { hasPermission } = useAuth();
  return hasPermission;
};
